import { ROLE_LABELS } from '@bo/constants/userRoles';
import BoAuthService from '@boServices/boAuthService';
import UserService from '@boServices/userService';
import { faLockKeyhole } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import InputText from '@sharedComponents/InputText';
import Loader from '@sharedComponents/Loader';
import Panel from '@sharedComponents/Panel';
import Select from '@sharedComponents/Select';
import { showToast } from '@sharedComponents/Toast';
import Typography from '@sharedComponents/Typography';
import { getSupportedLanguageOptions } from '@utils/languageNames';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const userService = new UserService();
const authService = new BoAuthService();

const LOCK_ICON_STYLE = { color: 'var(--text-color-neutral-tertiary)', fontSize: '12px' };

const FieldLabel = ({ label, locked = false }) => (
    <FlexBox gap="xxs" alignItems="center">
        <Typography color="neutral-primary" fontWeight="regular">
            {label}
        </Typography>
        {locked && <FontAwesomeIcon icon={faLockKeyhole} style={LOCK_ICON_STYLE} />}
    </FlexBox>
);

const Parameters = () => {
    const { t, i18n } = useTranslation('bo');

    const [loading, setLoading] = useState(true);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [roles, setRoles] = useState([]);
    const [language, setLanguage] = useState((i18n.language || 'fr').split('-')[0]);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);

    const initialProfile = useRef({ firstName: '', lastName: '' });
    const initialLanguage = useRef((i18n.language || 'fr').split('-')[0]);

    useEffect(() => {
        userService
            .getMe()
            .then((user) => {
                setFirstName(user.firstName ?? '');
                setLastName(user.lastName ?? '');
                setEmail(user.email ?? '');
                setRoles(user.roles ?? []);
                initialProfile.current = { firstName: user.firstName ?? '', lastName: user.lastName ?? '' };
            })
            .catch(() => {
                showToast({ type: 'error', text: t('An error occurred while loading your profile. Please try again.') });
            })
            .finally(() => setLoading(false));
    }, [t]);

    const languageOptions = getSupportedLanguageOptions(i18n);
    const roleLabel = roles.map((role) => t(ROLE_LABELS[role] ?? role)).join(', ');

    const isDirty =
        firstName !== initialProfile.current.firstName ||
        lastName !== initialProfile.current.lastName ||
        language !== initialLanguage.current;

    const handleSave = async () => {
        setSaving(true);
        try {
            if (firstName !== initialProfile.current.firstName || lastName !== initialProfile.current.lastName) {
                const updated = await userService.updateMe({ firstName: firstName.trim(), lastName: lastName.trim() });
                setFirstName(updated.firstName ?? '');
                setLastName(updated.lastName ?? '');
                initialProfile.current = { firstName: updated.firstName ?? '', lastName: updated.lastName ?? '' };
            }
            // Language preference stays browser-side: applied through i18n and persisted in localStorage.
            if (language !== initialLanguage.current) {
                i18n.changeLanguage(language);
                initialLanguage.current = language;
            }
            showToast({ type: 'success', text: t('Your changes have been saved.') });
        } catch {
            showToast({ type: 'error', text: t('An error occurred while saving. Please try again.') });
        } finally {
            setSaving(false);
        }
    };

    const handleResetPassword = async () => {
        setResetting(true);
        try {
            await authService.requestPasswordReset(email);
            showToast({ type: 'success', text: t('A reset link has been sent to your email address.') });
        } catch {
            showToast({ type: 'error', text: t('An error occurred while sending the reset email. Please try again.') });
        } finally {
            setResetting(false);
        }
    };

    return (
        <FlexBox flexDirection="column" gap="l" additionnalStyles={{ padding: 'var(--space-l) var(--space-xl)' }}>
            <Typography fontSize="xl" fontWeight="bold" color="branded-primary" seo={1}>
                {t('Parameters')}
            </Typography>

            <Panel>
                <FlexBox flexDirection="column" gap="l" additionnalStyles={{ padding: 'var(--space-m)' }}>
                    {loading ? (
                        <Loader />
                    ) : (
                        <>
                            <FlexBox gap="xl" flexWrap="wrap" alignItems="flex-start">
                                <FlexBox flexDirection="column" gap="m" additionnalStyles={{ flex: '1 1 260px' }}>
                                    <FlexBox flexDirection="column" gap="xxs">
                                        <FieldLabel label={t('First name')} />
                                        <InputText value={firstName} onChange={(event) => setFirstName(event.target.value)} />
                                    </FlexBox>

                                    <FlexBox flexDirection="column" gap="xxs">
                                        <FieldLabel label={t('Last name')} />
                                        <InputText value={lastName} onChange={(event) => setLastName(event.target.value)} />
                                    </FlexBox>

                                    <FlexBox flexDirection="column" gap="xxs">
                                        <FieldLabel label={t('Email address')} locked />
                                        <InputText type="email" value={email} disabled readOnly />
                                    </FlexBox>

                                    <FlexBox flexDirection="column" gap="xxs">
                                        <FieldLabel label={t('Password')} locked />
                                        <InputText type="password" value="************" disabled readOnly />
                                        <FlexBox>
                                            <Button label={t('Reset')} action={handleResetPassword} ghost noPadding isLoading={resetting} />
                                        </FlexBox>
                                    </FlexBox>
                                </FlexBox>

                                <FlexBox flexDirection="column" gap="m" additionnalStyles={{ flex: '1 1 260px' }}>
                                    <FlexBox flexDirection="column" gap="xxs">
                                        <FieldLabel label={t('Interface language')} />
                                        <Select
                                            options={languageOptions}
                                            value={language}
                                            onChange={(opt) => setLanguage(opt.value)}
                                            isSearchable={false}
                                        />
                                    </FlexBox>

                                    <FlexBox flexDirection="column" gap="xxs">
                                        <FieldLabel label={t('Role')} locked />
                                        <InputText value={roleLabel} disabled readOnly />
                                    </FlexBox>
                                </FlexBox>
                            </FlexBox>

                            <FlexBox>
                                <Button label={t('Save modifications')} disabled={!isDirty} isLoading={saving} action={handleSave} />
                            </FlexBox>
                        </>
                    )}
                </FlexBox>
            </Panel>
        </FlexBox>
    );
};

export default Parameters;
